import { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Popover } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { debounce, find, isEmpty, map } from 'lodash';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import { validateMinLength, validateMinNumber, validateNumber } from 'common/forms/formValidations';
import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';
import TextField from 'common/forms/TextField';
import { casingFormat } from 'common/utils';
import { listDrugProfilesByDrugName } from 'store/thunks/drugThunks';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { updateFillLaterFilter } from 'store/thunks/workQueueThunks';

import {
  FILL_LATER_STATUS_OPTIONS,
  FILL_LATER_TYPE_OPTIONS,
} from '../constants/fillLaterEligibility';
import { NDC_FILTER_TOOLTIP } from '../constants/filterTooltips';
import FacilityFilterField from './FacilityFilterField';

/**
 * Filters popover for the Fill Later queue. Fields are the Bulk tab's: Patient, Order Id and
 * Rx Number text, Drug Name (searched from drug profiles, as Bulk does), NDC (free-text list),
 * Qty, Partner / Practice (practices load from the selected partners), Facility, Status.
 * Serialisation matches Bulk so the backend receives ids, not labels.
 */
const FillLaterQueueFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState({});
  const [practices, setPractices] = useState([]);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  // POM-6149 feedback: free-text NDC list, same shape as the Bulk and Order Management filters.
  const [selectedNdcs, setSelectedNdcs] = useState([]);
  // POM-6149 feedback: Drug Name is the Bulk picker (drug profiles searched after 3 characters,
  // options sorted), not a free-text prefix. Selections go to the backend as drugNames.
  const [selectedDrugNames, setSelectedDrugNames] = useState([]);
  const [drugNameOptions, setDrugNameOptions] = useState([]);
  const [drugNameInputValue, setDrugNameInputValue] = useState('');
  const [drugLoading, setDrugLoading] = useState(false);

  const filter = useSelector(({ workQueue }) => workQueue.fillLater.filter);
  const loading = useSelector(({ workQueue }) => workQueue.fillLater.loading);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const partnerOptions = useMemo(
    () => map(partners, ({ partnerName }) => casingFormat(partnerName)),
    [partners]
  );

  const partnerIdsFor = (names) =>
    names
      .map(
        (name) =>
          find(partners, (p) => casingFormat(p.partnerName).toLowerCase() === name.toLowerCase())
            ?.partnerUuid
      )
      .filter(Boolean);

  const selectedPartnerIds = useMemo(
    () => partnerIdsFor(selectedPartners),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPartners, partners]
  );

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      setSelectedPractices([]);
      return;
    }
    setPracticeLoading(true);
    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds }))
      .then((result) => {
        if (result) setPractices(result);
      })
      .finally(() => setPracticeLoading(false));
  }, [selectedPartnerIds, dispatch]);

  // The status bar can clear the NDC chip while the popover is closed; keep the local list in step.
  useEffect(() => {
    if (isEmpty(filter?.ndcs)) {
      setSelectedNdcs([]);
    }
  }, [filter?.ndcs]);

  useEffect(() => {
    if (isEmpty(filter?.drugNames)) {
      setSelectedDrugNames([]);
      setDrugNameOptions([]);
      setDrugNameInputValue('');
    }
  }, [filter?.drugNames]);

  // Same search as the Bulk Drug Name filter: drug profiles by name, debounced, from 3 characters.
  const debouncedSearchDrugName = useMemo(() => {
    const searchFunc = (drugName) => {
      setDrugLoading(true);
      dispatch(listDrugProfilesByDrugName(drugName, { excludeDrugStatus: true }))
        .then((result) => {
          if (result) setDrugNameOptions(result);
        })
        .finally(() => setDrugLoading(false));
    };
    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, [dispatch]);

  useEffect(() => {
    if (drugNameInputValue.length >= 3) {
      debouncedSearchDrugName(drugNameInputValue);
    }
  }, [drugNameInputValue, debouncedSearchDrugName]);

  const getDrugNameNoOptionsText = () => {
    if (drugLoading) return 'Loading...';
    if (drugNameInputValue.length < 3) return 'Type at least 3 characters to search...';
    return 'No results found';
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    const partnerNames = map(
      (partners ?? []).filter((p) => filter?.partnerIds?.includes(p.partnerUuid)),
      (p) => casingFormat(p.partnerName)
    );
    setSelectedPartners(partnerNames);
    setSelectedNdcs(filter?.ndcs ?? []);
    setSelectedDrugNames(filter?.drugNames ?? []);

    setFormInitialValues({
      patientName: filter?.patientName ?? '',
      orderId: filter?.orderId ?? '',
      rxNumber: filter?.rxNumber ?? '',
      quantity: filter?.quantity ?? '',
      fulfillingPharmacyIds: filter?.fulfillingPharmacyIds ?? '',
      statuses: FILL_LATER_STATUS_OPTIONS.filter((opt) => filter?.statuses?.includes(opt.value)),
      fillLaterTypes: FILL_LATER_TYPE_OPTIONS.filter((opt) =>
        filter?.fillLaterTypes?.includes(opt.value)
      ),
    });
  };

  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'WorkQueue-FillLaterQueueFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    const practiceIds = selectedPractices
      .map(
        (name) =>
          find(practices, (p) => p.practiceName.toLowerCase() === name.toLowerCase())?.practiceId
      )
      .filter(Boolean);

    dispatch(
      updateFillLaterFilter({
        filter: {
          ...filter,
          patientName: formData.patientName || undefined,
          orderId: formData.orderId || undefined,
          rxNumber: formData.rxNumber || undefined,
          // the free-text prefix is gone from this form; clear any value an older session left
          drugName: undefined,
          drugNames: !isEmpty(selectedDrugNames) ? selectedDrugNames : undefined,
          ndcs: !isEmpty(selectedNdcs) ? selectedNdcs : undefined,
          // numeric as a string, the shape the entity and the queue use for split quantities
          quantity: formData.quantity ? String(formData.quantity).trim() : undefined,
          fulfillingPharmacyIds: !isEmpty(formData.fulfillingPharmacyIds)
            ? formData.fulfillingPharmacyIds
            : undefined,
          statuses: !isEmpty(formData.statuses) ? formData.statuses.map((s) => s.value) : undefined,
          fillLaterTypes: !isEmpty(formData.fillLaterTypes)
            ? formData.fillLaterTypes.map((t) => t.value)
            : undefined,
          partnerIds: !isEmpty(selectedPartnerIds) ? selectedPartnerIds : undefined,
          practiceIds: !isEmpty(practiceIds) ? practiceIds : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxHeight: '70vh', overflowY: 'auto' } } }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Patient'
                      name='patientName'
                      id='WorkQueue-FillLaterQueueFilterButton-patientName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order Id'
                      name='orderId'
                      id='WorkQueue-FillLaterQueueFilterButton-orderId-input'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Rx Number'
                      name='rxNumber'
                      id='WorkQueue-FillLaterQueueFilterButton-rxNumber-input'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Drug Name'
                      name='drugNames'
                      id='WorkQueue-FillLaterQueueFilterButton-drugName-input'
                      multiple
                      sortOptions
                      serverSideSearch
                      loading={drugLoading}
                      options={drugNameOptions.map((drug) => drug.drugName).filter(Boolean)}
                      defaultValue={selectedDrugNames}
                      value={selectedDrugNames}
                      onChange={(event, newValue) => setSelectedDrugNames(newValue)}
                      onInputChange={(event, value) => setDrugNameInputValue(value ?? '')}
                      noOptionsText={getDrugNameNoOptionsText()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='NDC'
                      name='ndcs'
                      id='WorkQueue-FillLaterQueueFilterButton-ndcs-input'
                      multiple
                      freeSolo
                      options={[]}
                      formatOption={(value) => value}
                      defaultValue={selectedNdcs}
                      value={selectedNdcs}
                      onChange={(event, newValue) => setSelectedNdcs(newValue)}
                      labelTooltip={NDC_FILTER_TOOLTIP}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Qty'
                      name='quantity'
                      id='WorkQueue-FillLaterQueueFilterButton-quantity-input'
                      type='number'
                      validations={[
                        validateNumber('Enter a valid quantity'),
                        validateMinNumber('Must be greater than 0', 0.001),
                      ]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Partner'
                      name='partnerNames'
                      id='WorkQueue-FillLaterQueueFilterButton-partnerIds-input'
                      multiple
                      sortOptions
                      defaultValue={selectedPartners}
                      options={partnerOptions}
                      value={selectedPartners}
                      onChange={(event, newValue) => {
                        setSelectedPartners(newValue);
                        setSelectedPractices([]);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      key={selectedPartnerIds.length}
                      label='Practice'
                      name='practiceNames'
                      id='WorkQueue-FillLaterQueueFilterButton-practiceIds-input'
                      multiple
                      sortOptions
                      defaultValue={selectedPractices}
                      disabled={!selectedPartnerIds.length}
                      loading={practiceLoading}
                      options={practices.map((practice) => casingFormat(practice.practiceName))}
                      value={selectedPractices}
                      onChange={(event, newValue) => setSelectedPractices(newValue)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <FacilityFilterField />
                  </Grid>

                  <MultiPracticeSelectField
                    label='Status'
                    name='statuses'
                    id='WorkQueue-FillLaterQueueFilterButton-statuses-dropdown'
                    options={FILL_LATER_STATUS_OPTIONS}
                  />

                  <MultiPracticeSelectField
                    label='Type'
                    name='fillLaterTypes'
                    id='WorkQueue-FillLaterQueueFilterButton-fillLaterTypes-dropdown'
                    options={FILL_LATER_TYPE_OPTIONS}
                  />

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default FillLaterQueueFilterButton;
